import React from 'react';

interface LifetimeTieredProgressBarProps {
  totalSold: number;
  maxPurchases?: number;
}

/**
 * Multi-segment progress bar showing the three lifetime pricing tiers.
 * - Tier 1 (0-25): $149.99 - Green segment (25% of bar)
 * - Tier 2 (26-75): $224.99 - Yellow segment (50% of bar)
 * - Tier 3 (76-100): $299.99 - Orange segment (25% of bar)
 */
export const LifetimeTieredProgressBar: React.FC<LifetimeTieredProgressBarProps> = ({
  totalSold,
  maxPurchases = 100,
}) => {
  // Tier boundaries
  const tier1Max = 25;
  const tier2Max = 75;
  const tier3Max = 100;

  // Calculate fill percentages for each tier
  const tier1Fill = Math.min((totalSold / tier1Max) * 100, 100);
  const tier2Fill = Math.min(Math.max((totalSold - tier1Max) / (tier2Max - tier1Max) * 100, 0), 100);
  const tier3Fill = Math.min(Math.max((totalSold - tier2Max) / (tier3Max - tier2Max) * 100, 0), 100);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex justify-between text-xs mb-2">
        <span>{totalSold}/{maxPurchases} lifetime spots claimed</span>
      </div>

      {/* Price labels above progress bar */}
      <div className="flex justify-between text-xs font-medium mb-1 px-1">
        <span className="text-green-600">$149.99</span>
        <span className="text-yellow-600">$224.99</span>
        <span className="text-orange-600">$299.99</span>
      </div>

      {/* Multi-segment progress bar */}
      <div className="w-full bg-muted rounded-full h-3 overflow-hidden flex">
        {/* Tier 1: 0-25 (25% of bar width) */}
        <div className="relative" style={{ width: '25%' }}>
          <div
            className="h-full bg-green-500 transition-all duration-500"
            style={{ width: `${tier1Fill}%` }}
          />
        </div>
        {/* Tier 2: 26-75 (50% of bar width) */}
        <div className="relative" style={{ width: '50%' }}>
          <div
            className="h-full bg-yellow-500 transition-all duration-500"
            style={{ width: `${tier2Fill}%` }}
          />
        </div>
        {/* Tier 3: 76-100 (25% of bar width) */}
        <div className="relative" style={{ width: '25%' }}>
          <div
            className="h-full bg-orange-500 transition-all duration-500"
            style={{ width: `${tier3Fill}%` }}
          />
        </div>
      </div>

      {/* Tier breakdown cards - proportional widths matching progress bar */}
      <div className="flex gap-2 mt-3 text-xs">
        <div className="text-center p-2 rounded bg-muted/50" style={{ width: '25%' }}>
          <div className="font-medium">Tier 1</div>
          <div className="text-muted-foreground">{Math.min(totalSold, tier1Max)}/{tier1Max}</div>
        </div>
        <div className="text-center p-2 rounded bg-muted/50" style={{ width: '50%' }}>
          <div className="font-medium">Tier 2</div>
          <div className="text-muted-foreground">{Math.max(0, Math.min(totalSold - tier1Max, tier2Max - tier1Max))}/{tier2Max - tier1Max}</div>
        </div>
        <div className="text-center p-2 rounded bg-muted/50" style={{ width: '25%' }}>
          <div className="font-medium">Tier 3</div>
          <div className="text-muted-foreground">{Math.max(0, totalSold - tier2Max)}/{tier3Max - tier2Max}</div>
        </div>
      </div>
    </div>
  );
};
